import { useMemo, useRef, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle } from '@editor/interfaces/canvas'
import { RectangleMeshProps } from '@editor/interfaces/canvas/rectangleMesh'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'
import { DEFAULT_LINE_MATERIAL } from '@editor/pages/projects/editor/objects/Polyline/constants'
import { BufferAttribute, BufferGeometry, Matrix4, Mesh, Quaternion, ShapeUtils, Vector2, Vector3 } from 'three'
import { Line2, LineGeometry } from 'three-stdlib'

const useObjectsManagement = ({ positions, offsetTransformation, vertices, outline }: RectangleMeshProps) => {
  // Refs
  const meshRef = useRef<Mesh | null>(null)

  const { position: offsetPosition } = useMemo(() => {
    if (!offsetTransformation) return { position: new Vector3(), quaternion: new Quaternion(), matrix: new Matrix4() }

    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(offsetTransformation)

    mtx.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: mtx,
    }
  }, [offsetTransformation])

  /**
   * Create rectangle geometry using BufferGeometry following HoverContainer pattern
   */
  const [rectangleGeometry] = useState(() => {
    const pts = positions || vertices?.map((v) => [...v, 0] as PointArray)

    if (!pts || pts.length < 4) return null

    const buffer = new BufferGeometry()
    const points = new Float32Array(pts.length * 3)
    pts.forEach((vertex, index) => {
      points.set(vertex, index * 3)
    })

    // Triangulate the rectangle (2 triangles)
    const indices = ShapeUtils.triangulateShape(
      pts.map((p) => new Vector2(p[0], p[1])),
      [],
    )

    buffer.setAttribute('position', new BufferAttribute(points, 3))
    buffer.setIndex(indices.flat())
    buffer.computeVertexNormals()
    buffer.computeBoundingBox()
    buffer.computeBoundingSphere()

    return buffer
  })

  /**
   * Create outline line object only once using useState to avoid recreation on every render.
   */
  const [outlineObject] = useState(() => {
    const pts = positions || vertices?.map((v) => [...v, 0] as PointArray)

    if (!outline?.color || !pts) return null

    const geometry = new LineGeometry()
    geometry.setPositions([...pts, pts[0]].flat())

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()
    outlineMat.dashed = outline.style === LineStyle.Dashed
    outlineMat.color.set(outline.color)
    outlineMat.linewidth = outline.thickness || 0
    outlineMat.dashSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO
    outlineMat.gapSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()

    return outlineLn
  })

  const [anchorPositions, setAnchorPositions] = useState<{ id: string; position: Vector3 }[]>(() => {
    if (positions && positions.length >= 4) {
      return positions.map((point) => ({
        id: crypto.randomUUID(),
        position: new Vector3(...point),
      }))
    }

    return []
  })

  return {
    meshRef,
    rectangleGeometry,
    outlineObject,

    offsetPosition,

    anchorPositions,
    setAnchorPositions,
  }
}

export default useObjectsManagement
